import { JobAd } from '/lib/components/jobAd/jobAd';
import {
	Alert,
	Card,
	Heading,
	Link,
	Paragraph,
	Tag,
} from '@digdir/designsystemet-react';
//import { sanitize } from '/assets/js/utils';
import { ComponentProps, PartData, RichText } from '@enonic/react-components';
import { ImageTextBox } from '/lib/components/imageTextBox/imageTextBox';
import './frontpage.css';

interface FrontpageData {
	heading: string;
	topMedia: ImageElement;
	title: string;
	title_part2: string;
	ingressHeading: string;
	ingressText: string;
	collagePics: ImageElement[];
	jobHeading: string;
	internalAdLink: string;
	moreThanXJobs: boolean;
	fewOrZeroJobs_txt: string;
	fewOrZeroJobs: string;
	jobAds: JobListing[];
	newsHeading: string;
	newsText: RichTextType;
	newsImageData: ImageElement | undefined;
	elements: FrontpageElement[];
}

type FrontpageElement = ImageWithTextBox;

export interface ImageWithTextBox {
	_selected: 'imageWithTextBox';
	title: string;
	text: string;
	url: string;
	imageData: ImageElement;
}

export interface ImageElement {
	altText: string;
	caption: string;
	url: string;
}

export interface JobListing {
	id: number;
	isJobListing: boolean;
	jobScope: string;
	jobDuration: string;
	deadline: string;
	employer: string;
	title: string;
	summary: string;
	logo: string;
	link: string;
	location: string;
	promoted: boolean;
	positionCount: number;
}

export interface RichTextType {
	processedHtml: string;
	macros: any[];
	images: any[];
	links: any[];
	macroComponents: any[];
}

const Gallery = ({ collagePics }) => {
	return (
		<div
			className="gallery"
			role="group"
			aria-label="bildekollasj/illustrasjon"
		>
			<div className="gallery-top">
				<img
					className="top-left"
					src={collagePics[0].imageData.url}
					alt=""
				/>
				<img
					className="top-right"
					src={collagePics[1].imageData.url}
					alt=""
				/>
			</div>
			<div className="gallery-bottom">
				<img
					className="bottom-left"
					src={collagePics[2].imageData.url}
					alt=""
				/>
				<img
					className="bottom-right"
					src={collagePics[3].imageData.url}
					alt=""
				/>
			</div>
		</div>
	);
};

export const Frontpage = (props: ComponentProps<PartData>) => {
	// console.log('props for Frontend.tsx: ' + JSON.stringify(props, null, 2));
	const {
		heading,
		topMedia,
		title,
		title_part2,
		ingressHeading,
		ingressText,
		collagePics,
		jobHeading,
		internalAdLink,
		moreThanXJobs,
		fewOrZeroJobs_txt,
		fewOrZeroJobs,
		jobAds,
		newsHeading,
		newsText,
		newsImageData,
		elements,
	} = props.data as any as FrontpageData;

	const hasError: boolean = props.data.jobAds === null;

	return (
		<>
			<main id="main-content">
				<h1 className="sr-only">{heading}</h1>
				<section className="top-wrapper">
					<div className="page-inner top">
						<div className="top-media">
							<img src={topMedia.url} alt="" />
							<Paragraph data-color-scheme="dark">
								{title}
								{title_part2?.trim().length > 0 && (
									<span className="top-media-title2">
										{title_part2}
									</span>
								)}
							</Paragraph>
						</div>
					</div>
					<div className="page-inner">
						<div className="gallery-wrapper">
							<div className="ingress">
								<Heading level={2}>{ingressHeading}</Heading>
								<p>{ingressText}</p>
							</div>
							<Gallery collagePics={collagePics} />
						</div>
					</div>
				</section>
				<section className="content-wrapper">
					<div className="page-inner">
						<Heading level={2}>{jobHeading}</Heading>
						<div className="job-ad-wrapper">
							{hasError ? (
								<Alert data-color="warning">
									Vi kan ikke vise stillingsannonser nå. Prøv
									igjen senere.
								</Alert>
							) : (
								<div className="jobs">
									{jobAds.length > 0 &&
										jobAds
											.slice(0, 3)
											.map((job) => (
												<JobAd key={job.id} {...job} />
											))}
								</div>
							)}
							<div className="jobLink">
								<Link
									href={
										jobAds.length > 3
											? internalAdLink
											: fewOrZeroJobs
									}
								>
									{jobAds.length > 3
										? moreThanXJobs
										: fewOrZeroJobs_txt}
								</Link>
							</div>
						</div>
					</div>
					{newsText.processedHtml.length > 0 && newsImageData && (
						<div className="news-wrapper page-inner">
							<Card>
								<Card.Block className="newsCardText">
									<Tag data-color="main1" data-size="md">
										Aktuelt nå
									</Tag>
									<Heading level={2}>{newsHeading}</Heading>
									<RichText data={newsText} />
								</Card.Block>
								<Card.Block className="newsCardImage">
									<img
										src={newsImageData.url}
										alt={newsImageData.altText}
									/>
								</Card.Block>
							</Card>
						</div>
					)}
					<div className="page-inner">
						{elements.map((element, index) => {
							switch (element._selected) {
								case 'imageWithTextBox':
									return (
										<section
											key={index}
											className="image-with-textbox"
										>
											<ImageTextBox
												props={element}
												index={index}
											/>
										</section>
									);
								default:
									return '';
							}
						})}
					</div>
				</section>
			</main>
		</>
	);
};
